<template>
  <div class="agency-card-container">
    <v-card
      class="modern-agency-card rounded-xl"
      :elevation="isHovered ? 3 : 1"
      @mouseover="isHovered = true"
      @mouseleave="isHovered = false"
      @click="goToProfile"
    >
      <!-- Loading state -->
      <v-skeleton-loader
        v-if="loading || !agency"
        type="image, list-item-two-line"
        class="pa-4"
      />
      <!-- Loaded state -->
      <template v-else>
        <!-- Card header with background and avatar -->
        <div class="card-header position-relative">
          <div class="header-bg">
            <img
              v-if="agency.bgImage"
              :src="agency.bgImage"
              alt="Agency Cover Image"
              class="w-100 h-100"
            />
            <div v-else class="default-bg"></div>
          </div>
          <div class="avatar-container">
            <agency-avatar
              :img="agency.midThumbnail || agency.image"
              :name="agency.name"
              :size="avatarSize"
              class="elevation-3"
            />
          </div>
        </div>

        <!-- Card content -->
        <div class="mt-2 text-center pa-4">
          <h3 class="mb-1 text-h6 font-weight-bold primary-color text-truncate">
            {{ agency.name }}
            <!-- <v-chip v-if="getCompletedInfo" small color="orange lighten-4" class="text-orange-darken-2">
            <v-icon small left>mdi-alert</v-icon>
            Incomplete
          </v-chip>
          <v-tooltip bottom v-if="getCompletedInfo">
            <template v-slot:activator="{ on }">
              <v-icon small color="grey darken-1" class="ml-1" v-on="on">mdi-alert-circle-outline</v-icon>
            </template>
            <span>Missing: {{ getCompletedInfo }}</span>
          </v-tooltip> -->
          </h3>

          <!-- <div class="justify-center mb-4 d-flex align-center">
            <v-icon small color="grey darken-1" class="mr-1"
              >mdi-account-group</v-icon
            >
            <span class="text-caption grey--text text--darken-1">
              {{
                (agency.followed_by?.length || 0) -
                  (agency.followed_by?.includes(agency._id) ? 1 : 0)
              }}
              followers
            </span>
          </div> -->

          <div class="social-stats mb-4">
              <div class="stat-item">
                <div class="stat-value">{{ agency.followed_by.length }}</div>
                <div class="stat-label">{{ agency.followed_by.length === 1 ? $t("follower") : $t("followers") }}</div>
              </div>
              <div class="stat-divider"></div>
              <div class="stat-item">
                <div class="stat-value">{{ agency.followers.length }}</div>
                <div class="stat-label">{{ $t("following") }}</div>
              </div>
            </div>

          <div class="d-flex justify-space-between">
            <v-btn
              :color="getFanned ? 'success' : '#0A5B8A'"
              :outlined="getFanned"
              :dark="!getFanned"
              rounded
              class="flex-grow-1"
              height="36"
              @click.stop="becameFan"
              :loading="loading"
            >
              <v-icon left size="18">{{
                getFanned ? "mdi-check" : "mdi-plus"
              }}</v-icon>
              {{ getFanned ? "Following" : "Follow" }}
            </v-btn>
          </div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import AgencyAvatar from "@/components/agency/AgencyAvatar.vue";

export default {
  components: { AgencyAvatar },
  props: {
    agency: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      isHovered: false
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile",
      type: "type"
    }),
    getCompletedInfo() {
      const missingFields = [];
      if (!this.agency.image) missingFields.push("Logo");
      if (!this.agency.who_we_are) missingFields.push("Who we are");
      if (!this.agency.what_we_do) missingFields.push("What we do");
      if (!this.agency.contact_person || !this.agency.contact_title)
        missingFields.push("Contact Info");
      if (!this.agency.phone) missingFields.push("Phone");
      if (!this.agency.address) missingFields.push("Address");
      if (!this.agency.city) missingFields.push("City");
      if (!this.agency.state) missingFields.push("State");
      if (!this.agency.zip) missingFields.push("Zip");

      return missingFields.join(", ");
    },
    getFanned() {
      if (!this.profile.followers) return false;
      return this.profile.followers.includes(this.agency._id);
    },
    avatarSize() {
      const bp = this.$vuetify.breakpoint;
      if (bp.xs) return 50;
      if (bp.sm) return 60;
      return 70;
    }
  },
  methods: {
    ...mapActions("agency", {
      followAgency: "followAgency"
    }),
    goToProfile() {
      console.log('Navigating to profile');
      this.$router.push({
        path: `/agency-profile/${this.agency._id}/events`,
        params: { id: this.agency._id }
      });
    },
    async becameFan() {
      this.loading = true;
      const wasFollowing = this.getFanned;

      try {
        await this.followAgency({
          followed: !wasFollowing,
          agency: this.agency._id
        });

        // Update local state immediately
        if (wasFollowing) {
          // Remove from followers when unfollowing
          this.agency.followed_by = this.agency.followed_by.filter(
            id => id !== this.profile._id
          );
        } else {
          // Add to followers when following
          this.agency.followed_by = [
            ...(this.agency.followed_by || []),
            this.profile._id
          ];
        }
      } catch (error) {
        this.$store.dispatch("notification/show", {
          text: error.response?.data?.message || "An error occurred",
          color: "error"
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.agency-card-container {
  width: 100%;
  max-width: 300px;
  margin: auto;
  padding: 0 8px;
}

.modern-agency-card {
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 12px !important;
}

.card-header {
  height: 100px;
  width: 100%;
  position: relative;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.default-bg {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0a5b8a 0%, #0a7b8a 100%);
}

.avatar-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, 0%);
  z-index: 2;
  border-radius: 50%;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.primary-color {
  color: #472583;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .modern-agency-card {
    padding: 12px;
  }
  .card-header {
    height: 90px;
  }
}

@media (max-width: 768px) {
  .agency-card-container {
    max-width: 90%;
  }
  .card-header {
    height: 80px;
  }
}

@media (max-width: 600px) {
  .agency-card-container {
    max-width: 100%;
    padding: 0 12px;
  }
  .card-header {
    height: 70px;
  }
  .avatar-container {
    top: 35px;
  }
  h3.text-h6 {
    font-size: 1rem;
  }
  .v-btn {
    font-size: 0.85rem;
  }
}
.v-btn {
  font-size: 13px;
}
/* Grid layout for multiple cards */
.agency-cards-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 0 auto;
  padding: 16px;
}

.agency-card-container {
  flex: 1 1 calc(50% - 16px);
  max-width: 300px;
}

@media (max-width: 768px) {
  .agency-card-container {
    flex: 1 1 100%;
    max-width: 100%;
  }
}


/* Modern social stats */
.social-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
}
  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 12px;
  }
    .stat-value {
      font-size: 1.2rem;
      font-weight: 700;
      color: #333;
      line-height: 1.2;
    }
    
    .stat-label {
      font-size: 0.75rem;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-top: 2px;
    }
  
  
  .stat-divider {
    width: 1px;
    height: 24px;
    background: rgba(0, 0, 0, 0.1);
  }

@media (max-width: 768px) {
  .volunteer-card-container {
    max-width: 90%;
  }
  .card-header {
    height: 80px;
  }
  .social-stats {
    gap: 8px;
  }
    .stat-item {
      padding: 0 8px;
    }
}

</style>
